import React, { useContext, useState, useEffect }  from 'react'
import { View, ScrollView, StyleSheet } from 'react-native'
import { Button, Text } from '@ui-kitten/components'
import { UserContext } from '../../global/UserProvider'
import { CartView } from '../../components/CartView'
import { format } from 'date-fns'
import * as Sentry from "@sentry/react-native"
import PlatformDependantSelect from '../../components/PlatformDependantSelect'
import { useTranslation } from 'react-i18next'
import { organizationAtom } from '../../global/AtomsProvider'
import { getLocalStoreDate } from '../../utils/timezoneFunctions'
import { useAtomValue } from 'jotai'
import { updateClientOrder } from '../../global/Firebase/firebase'
import { sanitizeForFirestore } from '../../global/Firebase/productSanitize'
import { clientOrderStatuses, resolveStatusOption, orderIsProcessed, statusId } from '../../utils/orderStatus'
import { WhatsAppButton } from '../../components/WhatsAppButton'


export default function ClientOrderDetailScreen( { navigation, route } ) {
  const { t } = useTranslation()
  // ATL-1103: one shared table, so this screen and ClientOrdersScreen can never disagree.
  const STATUSES = clientOrderStatuses(t)
  const { detail, editItemInData, sectionIndex, itemIndex } = route.params
  const organization = useAtomValue(organizationAtom)
  const [context] = useContext(UserContext)
  // ATL-1103: seed from whichever shape the read path returned. `detail?.status || STATUSES[0]`
  // put a BARE STRING into this state when the status had been flattened, which the Select could
  // not match to any option — so a cancelled order showed no selection and looked new.
  const [status, setStatus] = useState(() => resolveStatusOption(detail?.status, STATUSES) ?? STATUSES[0])

  // Log order detail for E2E testing
  useEffect(() => {
    if (detail?.id) {
      console.log('[ORDER_DETAIL_DATA]', JSON.stringify(sanitizeForFirestore({
        id: detail.id,
        name: detail.name,
        phone: detail.phone,
        status: statusId(detail.status),
        isProcessed: typeof detail.isProcessed === 'boolean' ? detail.isProcessed : orderIsProcessed(detail.status),
        store: detail.store,
        organizationId: context.user?.organizationId,
      })));
    }
  }, [detail])

  const saveStatus = async (newStatus) => {
    try {
      setStatus(newStatus)
      // `newStatus` always comes from STATUSES (the Select's own options), so it is a full map.
      // `isProcessed` is derived rather than read off it so an option table that ever loses the
      // flag cannot silently write `undefined` over a real boolean.
      let updatedData = {status: newStatus, isProcessed: orderIsProcessed(newStatus)}
      await updateClientOrder(detail, updatedData)
      editItemInData({...detail, ...updatedData}, sectionIndex, itemIndex)
    } catch(error) {
      Sentry.captureException(error)
    }
  }
  
  // The header line reflects the status currently selected, falling back to the order's own field
  // for a record whose status says nothing.
  const isProcessed = orderIsProcessed(status) ?? (typeof detail.isProcessed === 'boolean' ? detail.isProcessed : null)

  return (
    <ScrollView style={{flex: 1, paddingHorizontal: 20}}>
      <Text category='label' style={[styles.spacedItem, {marginTop: 20}]}>{t('clientOrders.name') + ': ' + detail.name}</Text>
      <Text category='label' style={styles.spacedItem}>{t('clientOrders.date') + ': ' + (detail.date ? format(getLocalStoreDate(detail.store || organization?.storeForMarketplace, detail.date?.toDate()), "d/M/y HH:mm") : t('clientOrders.now'))}</Text>
      <Text category='label' style={styles.spacedItem}>{t('clientOrders.phone') + ': ' + detail.phone}</Text>
      <Text category='label' status={isProcessed === false && 'danger'} style={styles.spacedItem}>{t('clientOrders.status') + ': ' + (isProcessed ? t('clientOrders.processed') : t('clientOrders.notProcessed'))}</Text>
      <PlatformDependantSelect
        data={STATUSES}
        selectedOption={status}
        onSelect={saveStatus}
        style={styles.spacedItem}
      />
      <CartView style={styles.spacedItem} cartItems={detail.cart} store={detail.store || organization?.storeForMarketplace} />   
      <WhatsAppButton appearance='outline' style={styles.spacedItem} phone={detail.phone}>
        {t('clientOrders.writeOnWhatsapp')}
      </WhatsAppButton>
      <Button appearance='outline' style={styles.spacedItem} onPress={() => {
        navigation.navigate('NewSale', { clientOrderDetail: detail })
      }}>
        {t('clientOrders.convertIntoSale')}
      </Button>
      <View style={{height: 50}}></View>
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  spacedItem: {
    marginVertical: 4,
  },
})